import { getConfig } from "../config";
import { readSessionToken } from "./authToken";

// Typed REST client for the app-service REST API (docs/design/03-interface-contracts.md
// + api/openapi.yaml). Returns parsed problem-details for error responses
// (RFC-9457 / design/10 §5).

export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  correlationId?: string;
}

export class ApiError extends Error {
  /** HTTP status of the failed response (0 if it could not be determined). */
  readonly status: number;
  constructor(public problem: Problem, status?: number) {
    super(problem.title || problem.detail || "API error");
    this.status = status ?? problem.status ?? 0;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
  extraHeaders?: Record<string, string>
): Promise<T> {
  const cfg = getConfig();
  const bearer = token ?? readSessionToken(
    typeof window === "undefined" ? undefined : new URLSearchParams(window.location.search)
  );
  const res = await fetch(cfg.apiBaseUrl + path, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...(extraHeaders ?? {})
    },
    body: body == null ? undefined : JSON.stringify(body)
  });
  if (!res.ok) {
    // Authorization failures (and many ASP.NET status results) return an empty body, so the
    // parsed problem is {} with no title/detail. Carry the HTTP status so callers can render
    // a precise message (e.g. 403 -> "admin permission required") instead of a generic one.
    const problem = (await res.json().catch(() => ({}))) as Problem;
    throw new ApiError(problem, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get:  <T>(path: string, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("GET", path, undefined, token, extraHeaders),
  post: <T>(path: string, body?: unknown, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("POST", path, body, token, extraHeaders),
  put:  <T>(path: string, body?: unknown, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("PUT", path, body, token, extraHeaders),
  delete: <T>(path: string, token?: string, extraHeaders?: Record<string, string>) =>
    request<T>("DELETE", path, undefined, token, extraHeaders)
};
